/**
 * Iter 145bt — Daily 25 Double Chance slate panel.
 *
 * Lean parallel of HalftimeBetslipPanel — DC needs no calibration/
 * reputation layer (it's the platform's strongest market at ~70%
 * historical hit-rate), so the panel just fetches today's slate and
 * renders the top 25 picks with HIT/MISS/PENDING chips and an analysis
 * deep-link per row.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import {
  CheckCircle2, XCircle, MinusCircle, Clock as ClockIcon,
  TrendingUp, Loader2, ShieldCheck, ChevronRight, ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import MatchAnalysisModal from "./MatchAnalysisModal";
import { appTodayIso } from "../lib/appTime";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T12:00:00Z").toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric",
    });
  } catch { return iso; }
};
const fmtKickoff = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
};

const OutcomeChip = ({ outcome }) => {
  if (!outcome || outcome === "pending") {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border text-amber-300 border-amber-500/30 bg-amber-500/5">
        <ClockIcon className="w-2.5 h-2.5" /> Pending
      </span>
    );
  }
  const cfg = outcome === "hit"
    ? { Icon: CheckCircle2, cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", label: "Hit" }
    : outcome === "miss"
    ? { Icon: XCircle, cls: "text-rose-300 border-rose-500/40 bg-rose-500/10", label: "Miss" }
    : { Icon: MinusCircle, cls: "text-slate-300 border-slate-500/40 bg-slate-500/10", label: "Void" };
  const { Icon, cls, label } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${cls}`}>
      <Icon className="w-2.5 h-2.5" />{label}
    </span>
  );
};

export default function DoubleChanceBetslipPanel() {
  const [data, setData] = useState(null);
  const [weekStrip, setWeekStrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDate, setActiveDate] = useState(null);  // null = today
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoadingFid, setAnalysisLoadingFid] = useState(null);

  const loadDay = useCallback(async (date) => {
    setLoading(true);
    try {
      const url = date
        ? `${API}/double-chance/day/${date}`
        : `${API}/double-chance/today`;
      const r = await axios.get(url, { timeout: 30000 });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load Double Chance slate");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDay(activeDate); }, [loadDay, activeDate]);

  useEffect(() => {
    (async () => {
      try {
        // Iter 275f — 7 → 14 days so the archived hit tracker
        // surfaces the previous ~12 days on the horizontally-scrolling
        // strip (was yesterday + today + 5 future).
        const r = await axios.get(`${API}/double-chance/week?days=30`);
        setWeekStrip(r.data);
      } catch {}
    })();
  }, []);

  const openAnalysis = useCallback(async (fid) => {
    if (!fid) return;
    setAnalysisLoadingFid(fid);
    try {
      const { data: a } = await axios.get(
        `${API}/predictions/${fid}/analysis`,
        { timeout: 30000 }
      );
      setAnalysisData(a);
      setAnalysisOpen(true);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) toast.error("Analysis not available yet for this fixture.");
      else toast.error("Failed to load match analysis");
    } finally {
      setAnalysisLoadingFid(null);
    }
  }, []);

  const s = data?.summary || {};
  const picks = data?.picks || [];
  const todayIso = appTodayIso();

  return (
    <div className="space-y-6 mb-8" data-testid="double-chance-panel">
      {/* Hero summary */}
      <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-background to-background p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-300/90">
              <ShieldCheck className="w-3 h-3" />
              Daily 25 · Double Chance · Top market
            </span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mt-1.5">
              25 strongest Double Chance picks · {fmtDate(data?.date || todayIso)}
            </h2>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-xl">
              Double Chance is the platform's highest-converting market.
              All picks here are rebuilt daily and graded the moment full-time scores land.
            </p>
          </div>
        </div>
        {data && picks.length > 0 && (
          <dl
            className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5 pt-5 border-t border-emerald-500/20"
            data-testid="double-chance-summary"
          >
            <div>
              <dt className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Total picks</dt>
              <dd className="font-display text-2xl font-bold tabular-nums">{picks.length}</dd>
            </div>
            <div>
              <dt className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Hits</dt>
              <dd className="font-display text-2xl font-bold tabular-nums text-emerald-300">{s.hit || 0}</dd>
            </div>
            <div>
              <dt className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Misses</dt>
              <dd className="font-display text-2xl font-bold tabular-nums text-rose-300">{s.miss || 0}</dd>
            </div>
            <div>
              <dt className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Pending</dt>
              <dd className="font-display text-2xl font-bold tabular-nums text-amber-300/90">{s.pending || 0}</dd>
            </div>
            <div>
              <dt className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Hit rate</dt>
              <dd className="font-display text-2xl font-bold tabular-nums text-emerald-200">
                {s.hit_rate != null ? `${s.hit_rate}%` : "—"}
              </dd>
            </div>
          </dl>
        )}
      </div>

      {/* Week strip — day chips */}
      {weekStrip?.days?.length > 0 && (
        <div
          className="flex items-center gap-2 overflow-x-auto pb-1"
          data-testid="double-chance-week-strip"
        >
          {weekStrip.days.map((d) => {
            const isActive = (activeDate || todayIso) === d.date;
            const isToday = d.date === todayIso;
            const ds = d.summary || {};
            return (
              <button
                key={d.date}
                type="button"
                onClick={() => setActiveDate(d.date === todayIso ? null : d.date)}
                data-testid={`double-chance-day-${d.date}`}
                className={`shrink-0 px-3 py-2 rounded-xl border text-left transition-colors ${
                  isActive
                    ? "border-foreground bg-foreground text-background"
                    : "border-border bg-card/40 hover:bg-card/70"
                }`}
              >
                <div className="text-[9px] uppercase tracking-[0.16em] font-bold opacity-80">
                  {fmtDate(d.date)}{isToday && " · Today"}
                </div>
                <div className="text-[11px] font-bold tabular-nums mt-0.5">
                  {d.n_picks > 0 ? `${d.n_picks} picks` : `${d.fixtures_scheduled} fx`}
                  {ds.hit_rate != null && <span className="ml-1 text-emerald-300">· {ds.hit_rate}%</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Picks list */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-28 rounded-xl border border-border bg-card/30 animate-pulse" />
          ))}
        </div>
      ) : picks.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card/20 p-10 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            No Double Chance picks compiled for this day yet.  Slates rebuild daily at 23:35 UTC.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="double-chance-picks">
          {picks.map((p) => {
            const pick = p.pick || {};
            const home = p.home_team || {};
            const away = p.away_team || {};
            const league = p.league || {};
            const isLoading = analysisLoadingFid === p.fixture_id;
            return (
              <article
                key={p.fixture_id}
                className="rounded-xl border border-border bg-card/50 p-3.5 hover:bg-card/70 hover:border-foreground/30 transition-colors"
                data-testid={`double-chance-pick-${p.fixture_id}`}
              >
                <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  <span className="truncate flex-1">{league.name || "—"}</span>
                  <span className="inline-flex items-center gap-1 shrink-0">
                    <ClockIcon className="w-3 h-3" />
                    {fmtKickoff(p.kickoff)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mb-3 min-w-0">
                  <span className="font-semibold truncate flex-1 text-sm">{home.name || "Home"}</span>
                  <span className="text-[10px] uppercase text-muted-foreground tracking-wider shrink-0">vs</span>
                  <span className="font-semibold truncate flex-1 text-right text-sm">{away.name || "Away"}</span>
                </div>
                <div className="rounded-lg border border-border/50 bg-background/40 px-3 py-2 mb-2.5">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-emerald-300/80 truncate">
                      Double Chance · {pick.dc_code || "—"}
                    </span>
                    <OutcomeChip outcome={p.outcome} />
                  </div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-display font-bold truncate text-base">{pick.prediction || "—"}</span>
                    <span className="font-bold tabular-nums text-emerald-300 text-base shrink-0">
                      {pick.confidence != null ? `${Math.round(pick.confidence)}%` : "—"}
                    </span>
                  </div>
                  {p.actual_score && (
                    <div className="text-[10px] text-muted-foreground mt-1 font-mono">
                      FT {p.actual_score}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => openAnalysis(p.fixture_id)}
                  disabled={isLoading}
                  data-testid={`double-chance-pick-${p.fixture_id}-analyze`}
                  className="w-full inline-flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-300 hover:opacity-80 py-1.5 rounded-md border border-emerald-500/30 hover:bg-card/60 transition-colors disabled:opacity-60"
                >
                  {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />}
                  Deep analysis
                </button>
              </article>
            );
          })}
        </div>
      )}

      {analysisOpen && analysisData && (
        <MatchAnalysisModal
          analysis={analysisData}
          onClose={() => { setAnalysisOpen(false); setAnalysisData(null); }}
        />
      )}
    </div>
  );
}
